import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { Icon } from 'views/components/molecules/Icon';
import { InputText } from 'views/components/atoms/InputText';
import { MAX_WIDTH } from 'views/constants';
import { Button } from 'views/components/atoms/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import { localeIds } from 'locals';
import { HeaderMenu } from 'views/components/atoms/HeaderMenu';
import { Spacer } from 'views/components/atoms/Spacer';
import { ErrorMessage } from 'views/components/molecules/ErrorMessage';
import { validateEmailAddress } from 'utils/string';

const InputWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
`;

const MessageWrapper = styled.div`
  padding-top: 16px;
  display: flex;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  max-width: ${MAX_WIDTH};
  margin: auto;
`;

interface NotificationEmailFormProps {
  currentEmail?: string;
  onSave(email: string): void;
  onDelete(): void;
}

type EmailValidator = (email: string) => string | undefined;

export const NotificationEmailForm: React.FC<NotificationEmailFormProps> = ({
  currentEmail,
  onSave,
  onDelete,
}) => {
  const intl = useIntl();
  const [email, setEmail] = useState(currentEmail || '');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // email validation を送信時にしか行わないのであれば、email の変化に合わせる必要はないので、メールアドレスを引数で取る
  const validateEmail = useCallback<EmailValidator>(
    (email: string) => {
      if (validateEmailAddress(email)) {
        return intl.formatMessage({
          id: localeIds.emailValidation.invalid.format,
        });
      } else {
        return undefined;
      }
    },
    [intl]
  );

  const handleSubmit = useCallback((): void => {
    // 送信時にだけバリデーションを走らせる
    const validationErrorMessage = validateEmail(email);

    if (validationErrorMessage) {
      setErrorMessage(validationErrorMessage);
    } else {
      onSave(email);
    }
  }, [onSave, validateEmail, email]);

  return (
    <>
      <HeaderMenu>
        <InputWrapper>
          <InputText
            label={<Icon type="mail" />}
            onChange={setEmail}
            defaultValue={email}
            placeholder={intl.formatMessage({
              id: localeIds.notificationSettingPageLayout.placeholder,
            })}
            type="email"
            required
          />
        </InputWrapper>
      </HeaderMenu>

      {errorMessage ? (
        <MessageWrapper>
          <ErrorMessage message={errorMessage} maxWidth={MAX_WIDTH} />
        </MessageWrapper>
      ) : null}

      <Spacer />

      <ButtonWrapper>
        <Button
          type="submit"
          style="primary"
          disabled={currentEmail === email || email === ''}
          onClick={handleSubmit}
        >
          <FormattedMessage
            id={localeIds.notificationSettingPageLayout.submit}
          />
        </Button>

        {currentEmail ? (
          <>
            <Spacer />
            <Button type="submit" style="secondary" onClick={onDelete}>
              <FormattedMessage
                id={localeIds.notificationSettingPageLayout.remove}
              />
            </Button>
          </>
        ) : null}
      </ButtonWrapper>
    </>
  );
};
